/**
 * Results Page - Display QVT analysis results with visualizations
 */

import { Chart } from 'chart.js/auto';
import { AlertCircle, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageContent } from '../components/PageContent';
import { PageHeader } from '../components/PageHeader';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getResults, type ResultsData, type Timepoint } from '../lib/api';
import { percentTumorBurden } from '../lib/chart';
import { PlotlyVisualization } from '../components/PlotlyVisualization';
import { vizDisplayName } from '../lib/visualization';

export function ResultsPage() {
  const { id: analysisId } = useParams<{ id: string }>();

  const [results, setResults] = useState<ResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageModal, setImageModal] = useState<string | null>(null);
  const [vizModal, setVizModal] = useState<{ tp: Timepoint; selected: string } | null>(null);
  const [videoModal, setVideoModal] = useState<string | null>(null);

  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    async function loadResults() {
      if (!analysisId) {
        setLoading(false);
        return;
      }

      try {
        const data = await getResults(analysisId);
        setResults(data);
      } catch (error) {
        console.error('Failed to load results:', error);
      } finally {
        setLoading(false);
      }
    }

    void loadResults();
  }, [analysisId]);

  useEffect(() => {
    if (!results || !chartRef.current) {
      return;
    }

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) {
      return;
    }

    const labels = results.timepoints.map((tp) => tp.label);
    const qvtScores = results.timepoints.map((tp) => tp.qvt_score);
    const tumorBurdens = results.timepoints.map((tp) => tp.tumor_burden);
    const percentTumorBurdens = percentTumorBurden(tumorBurdens);

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'QVT Score',
            data: qvtScores,
            borderColor: 'rgb(33, 115, 0)',
            backgroundColor: 'rgb(33, 115, 0)',
            borderWidth: 1,
            yAxisID: 'y',
          },
          {
            label: 'Tumor Burden (% change)',
            data: percentTumorBurdens,
            borderColor: 'rgb(30, 100, 180)',
            backgroundColor: 'rgb(30, 100, 180)',
            borderWidth: 1,
            yAxisID: 'y1',
          },
        ],
      },
      options: {
        responsive: true,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
              pointStyle: 'rect',
              boxWidth: 8,
              boxHeight: 8,
              borderRadius: 2,
            },
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'QVT Score',
              color: 'rgb(33, 115, 0)',
            },
            ticks: {
              color: 'rgb(33, 115, 0)',
            },
            min: 0,
            max: 1,
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Tumor Burden',
              color: 'rgb(30, 100, 180)',
            },
            ticks: {
              color: 'rgb(30, 100, 180)',
              callback: function (value, _index, _ticks) {
                return value + '%';
              },
            },
            grid: {
              drawOnChartArea: false,
            },
            min: -100,
            grace: '5%',
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [results]);

  const getVizOptions = (tp: Timepoint) => {
    return Object.entries(tp.visualizations)
      .filter(([key, value]) => {
        if (key === 'tumor_burden_video') {
          return false;
        }
        return typeof value === 'object' && value.html;
      })
      .map(([key]) => key);
  };

  return (
    <Layout>
      <PageHeader
        title={results?.patient_id ? `Patient: ${results.patient_id}` : 'Patient Results'}
        description="QVT analysis results and visualizations"
      />
      <PageContent width="xl">
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Loading patient results...</p>
            </CardContent>
          </Card>
        ) : !results?.timepoints ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to load patient results. Please try again.</AlertDescription>
          </Alert>
        ) : (
          <>
            <Card>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-[30%_70%] gap-6">
                  {/* Left Column: Timepoint Data */}
                  <div className="space-y-4 min-w-0">
                    <CardTitle className="text-lg font-semibold">Time Points</CardTitle>
                    {results.timepoints.map((tp, idx) => {
                      const vizOptions = getVizOptions(tp);
                      // Handle tumor_burden_video as either string or {mp4: string}
                      const tumorVideo = tp.visualizations.tumor_burden_video;
                      const videoUrl =
                        typeof tumorVideo === 'string' ? tumorVideo : (tumorVideo?.mp4 ?? null);

                      return (
                        <div key={idx} className="data-row flex gap-3">
                          <div className="flex-1 min-w-0">
                            <button
                              type="button"
                              className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
                              onClick={() => {
                                if (videoUrl) {
                                  setVideoModal(videoUrl);
                                } else {
                                  setImageModal(tp.img_radiology);
                                }
                              }}
                            >
                              <img
                                className="img-radiology w-full rounded-md cursor-pointer transition-all hover:opacity-75 hover:scale-[1.02] hover:shadow-md"
                                src={tp.img_radiology}
                                alt="Radiology"
                              />
                            </button>
                            <div className="caption-scan text-sm text-center mt-1 text-muted-foreground">
                              {tp.label}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <button
                              type="button"
                              className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
                              onClick={() => {
                                if (vizOptions.length > 0) {
                                  setVizModal({ tp, selected: vizOptions[0] });
                                }
                              }}
                            >
                              <img
                                className="img-viz w-full rounded-md cursor-pointer transition-all hover:opacity-75 hover:scale-[1.02] hover:shadow-md"
                                src={tp.img_qvt}
                                alt="QVT"
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right Column: Chart */}
                  <div className="space-y-6 min-w-0">
                    <CardTitle className="text-lg font-semibold">QVT Scores</CardTitle>
                    <div className="pr-4">
                      <canvas
                        className="rounded-md shadow-sm border"
                        ref={chartRef}
                        id="qvt-chart"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </PageContent>

      {/* Image Modal - Lightbox */}
      <Dialog open={!!imageModal} onOpenChange={() => setImageModal(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] md:max-w-6xl p-0 overflow-hidden bg-black/95 border-0 [&>button]:hidden">
          <div className="absolute right-4 top-4 z-10">
            <DialogClose asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-0 bg-white text-black hover:bg-white/90"
              >
                <X />
                Close
              </Button>
            </DialogClose>
          </div>
          <div className="flex items-center justify-center w-full h-full p-4 md:p-8">
            <img
              src={imageModal ?? ''}
              alt="Full size radiology scan"
              className="max-w-full max-h-[90vh] object-contain rounded-sm"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Visualization Modal */}
      <Dialog open={!!vizModal} onOpenChange={() => setVizModal(null)}>
        <DialogContent className="max-w-[98vw] md:max-w-[95vw] lg:max-w-[95vw] w-[98vw] md:w-[95vw] max-h-[96vh] h-[96vh] flex flex-col p-0 gap-0 [&>button]:hidden">
          <DialogHeader className="flex-shrink-0 p-4 pr-1 pb-3 border-b">
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="text-base">{vizModal?.tp.label}</DialogTitle>
              <div className="flex items-center gap-2">
                <Select
                  value={vizModal?.selected}
                  onValueChange={(value) => {
                    if (vizModal) {
                      setVizModal({ ...vizModal, selected: value });
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px] h-8 text-sm" id="select-viz">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {vizModal &&
                      getVizOptions(vizModal.tp).map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {vizDisplayName(opt)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <DialogClose asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <X />
                    Close
                  </Button>
                </DialogClose>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
            {/* Main content area - scrollable container */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <div className="min-h-full flex flex-col items-center justify-center gap-4">
                <PlotlyVisualization
                  src={
                    vizModal && typeof vizModal.tp.visualizations[vizModal.selected] === 'object'
                      ? (vizModal.tp.visualizations[vizModal.selected] as { html: string }).html
                      : ''
                  }
                  className="border-0 w-full aspect-[4/3] max-h-[70vh] rounded-md bg-background"
                  title="Visualization"
                  autoRotate={true}
                  rotationSpeed={0.6}
                />
                {/* Feature table below iframe on mobile/tablet */}
                <div className="w-full lg:hidden space-y-2 bg-muted/30 p-4 rounded-md">
                  <h3 className="font-semibold text-sm">Feature Categories</h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between py-1 border-b">
                      <span>Torsion</span>
                      <span className="font-medium">241</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>Torsion Gradient</span>
                      <span className="font-medium">100</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>Curvature</span>
                      <span className="font-medium">220</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>Curvature Gradient</span>
                      <span className="font-medium">100</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>Vessel Radius</span>
                      <span className="font-medium">110</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Vessel Volume</span>
                      <span className="font-medium">3</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Feature table sidebar - desktop only */}
            <div className="hidden lg:block flex-shrink-0 w-64 border-l bg-muted/30 p-4 overflow-y-auto">
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Feature Categories</h3>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between py-1 border-b">
                    <span>Torsion</span>
                    <span className="font-medium">241</span>
                  </div>
                  <div className="flex justify-between py-1 border-b">
                    <span>Torsion Gradient</span>
                    <span className="font-medium">100</span>
                  </div>
                  <div className="flex justify-between py-1 border-b">
                    <span>Curvature</span>
                    <span className="font-medium">220</span>
                  </div>
                  <div className="flex justify-between py-1 border-b">
                    <span>Curvature Gradient</span>
                    <span className="font-medium">100</span>
                  </div>
                  <div className="flex justify-between py-1 border-b">
                    <span>Vessel Radius</span>
                    <span className="font-medium">110</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>Vessel Volume</span>
                    <span className="font-medium">3</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Modal - Lightbox */}
      <Dialog open={!!videoModal} onOpenChange={() => setVideoModal(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] md:max-w-6xl p-0 overflow-hidden bg-black/95 border-0 [&>button]:hidden">
          <div className="absolute right-4 top-4 z-10">
            <DialogClose asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-0 bg-white text-black hover:bg-white/90"
              >
                <X />
                Close
              </Button>
            </DialogClose>
          </div>
          <div className="flex items-center justify-center w-full h-full p-4 md:p-8">
            <video
              src={videoModal ?? ''}
              controls
              className="max-w-full max-h-[90vh] rounded-sm"
              autoPlay
              loop
            />
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
